import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';

import { Box, ButtonBase, Theme, Typography } from '@mui/material';
import { SxProps } from '@mui/system';

const LOGIN_STRINGS = appStrings.modules.login;

export type LoginMethod = 'phone' | 'email';

type LoginMethodTabsProps = Readonly<{
  value: LoginMethod;
  onChange: (method: LoginMethod) => void;
}>;

const TABS: readonly { method: LoginMethod; label: string; id: string }[] = [
  { method: 'email', label: LOGIN_STRINGS.emailTab, id: 'login-method-email-tab' },
  { method: 'phone', label: LOGIN_STRINGS.phoneTab, id: 'login-method-phone-tab' },
];

export const LoginMethodTabs = ({ value, onChange }: LoginMethodTabsProps) => {
  const activeIndex = Math.max(
    0,
    TABS.findIndex((tab) => tab.method === value),
  );

  return (
    <Box role="tablist" sx={tablistSx}>
      {/* Sliding thumb sits behind the labels and animates between tab slots. */}
      <Box aria-hidden sx={thumbSx(activeIndex)} />
      {TABS.map(({ method, label, id }) => {
        const isActive = value === method;

        return (
          <ButtonBase
            key={method}
            id={id}
            data-ph-capture-attribute-name={id}
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(method)}
            disableRipple
            sx={tabSx}
          >
            <Typography
              variant="text-sm-semibold"
              sx={{ transition: 'color 0.2s ease' }}
              color={(theme) =>
                theme.backrs.colors[
                  isActive ? 'Colors/Text/text-secondary' : 'Colors/Text/text-quaternary'
                ]
              }
            >
              {label}
            </Typography>
          </ButtonBase>
        );
      })}
    </Box>
  );
};

const tablistSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  justifyContent: 'center',
  gap: backrs.spacing['spacing-xxs'],
  backgroundColor: backrs.colors['Colors/Background/bg-secondary-alt'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,
  borderRadius: backrs.radius['radius-md'],
});

const thumbSx =
  (activeIndex: number): SxProps<Theme> =>
  ({ backrs }) => {
    const gap = backrs.spacing['spacing-xxs'];

    return {
      position: 'absolute',
      top: 0,
      bottom: 0,
      left: 0,
      width: `calc(50% - ${gap} / 2)`,
      borderRadius: backrs.radius['radius-md'],
      backgroundColor: backrs.colors['Colors/Base/White'],
      border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
      boxShadow: backrs.shadows['shadow-xs'],
      transform: activeIndex === 0 ? 'none' : `translateX(calc(100% + ${gap}))`,
      transition: 'transform 0.2s ease',
      '@media (prefers-reduced-motion: reduce)': {
        transition: 'none',
      },
    };
  };

const tabSx: SxProps<Theme> = ({ backrs }) => ({
  position: 'relative',
  zIndex: 1,
  flex: '1 0 0',
  minWidth: 0,
  height: '36px',
  px: backrs.spacing['spacing-lg'],
  py: backrs.spacing['spacing-md'],
  borderRadius: backrs.radius['radius-md'],
});
